import {
  Box,
  Button,
  VStack,
  Text as ChakraText,
  HStack,
  Link,
} from '@chakra-ui/react'
import { HiOutlineLightBulb } from 'react-icons/hi'
import { IoSquare } from 'react-icons/io5'
import React, { useContext } from 'react'
import { IoIosFlash } from 'react-icons/io'
import { Trans, useTranslation } from 'react-i18next'
import { colors } from '../../../ui-kit'
import { UserContext } from '../../providers'
import SDKIntegrationGrid from '../SDKIntegrationGrid'

interface Props {
  urlPrefix: string
}

const FlashBullet = (): React.ReactElement => (
  <Box
    bgColor={colors.appSlate}
    borderRadius="25px"
    width="16px"
    height="16px"
    padding="2.4px 2px 2.5px 3px"
  >
    <IoIosFlash fontSize="11px" color={colors.appPaleGrey} />
  </Box>
)

const ExampleEndpointUsage = (props: Props): React.ReactElement => {
  const { urlPrefix } = props
  const { user } = useContext(UserContext)
  const { t } = useTranslation()

  return (
    <Box width="full" padding="2em" bgColor="appWhiteSmoke">
      <VStack spacing="2em" alignItems="start">
        <Button
          borderStyle="none"
          as={Box}
          size="sm"
          borderRadius="25px"
          leftIcon={
            <HiOutlineLightBulb fontSize="16px" color="white" fill="white" />
          }
          bgColor={colors.appWatermelon}
        >
          <ChakraText color="white" fontWeight="semibold">
            {t('example_use_upper_case', 'EXAMPLE USE')}
          </ChakraText>
        </Button>

        <ChakraText
          color={colors.appDarkIndigo}
          fontSize="lg"
          fontWeight="medium"
        >
          {t('example_use_of_endpoint', 'Example Use of URL Endpoint')}
        </ChakraText>

        <Box>
          <ChakraText display="inline" color={colors.appBattleshipGrey}>
            {t(
              'if_original_image_accessible',
              'If the original image is accessible at ',
            )}
          </ChakraText>
          <ChakraText display="inline" color={colors.appBlue}>
            https://www.example.com/rest/of/the/path/image.jpg
          </ChakraText>
          <ChakraText display="inline" color={colors.appBattleshipGrey}>
            {t(
              'then_same_image_can_be_accessed',
              ', then the same image can be accessed through ImageKit.io URL ',
            )}
          </ChakraText>
          <ChakraText display="inline" color={colors.appBlue}>
            {`${user?.baseUrlForClientImageFetch}/${
              urlPrefix ? `${urlPrefix}/` : ''
            }rest/of/the/path/image.jpg`}
          </ChakraText>
        </Box>

        {/* Additional spacer */}
        <Box />

        <VStack
          alignItems="start"
          spacing="0.5em"
          bgColor="white"
          padding="1em"
        >
          <ChakraText color={colors.appBattleshipGrey}>
            {t('url_breakup', 'URL Breakup')}
          </ChakraText>

          <Box padding="1em 0.5em" bgColor={colors.appPaleGrey}>
            <ChakraText display="inline" color={colors.appDarkIndigo}>
              {`${user?.baseUrlForClientImageFetch}/${
                urlPrefix ? `${urlPrefix}/` : ''
              }`}
            </ChakraText>
            <ChakraText display="inline" color={colors.appGreenishTeal}>
              rest/of/the/path/image.jpg
            </ChakraText>
            <ChakraText display="inline" color={colors.appYellowOrange}>
              ?tr=w-300,h-300
            </ChakraText>
          </Box>
        </VStack>

        <VStack alignItems="start" spacing="0.5em">
          <HStack spacing="1em">
            <IoSquare color={colors.appDarkIndigo} />
            <ChakraText fontWeight="500" color={colors.appDarkIndigo}>
              {t('url_endpoint', 'URL Endpoint')}
            </ChakraText>
          </HStack>
          <HStack spacing="1em">
            <IoSquare color={colors.appGreenishTeal} />
            <ChakraText fontWeight="500" color={colors.appGreenishTeal}>
              {t('image_path', 'Image Path')}
            </ChakraText>
          </HStack>
          <HStack spacing="1em">
            <IoSquare color={colors.appYellowOrange} />
            <ChakraText fontWeight="500" color={colors.appYellowOrange}>
              {t('transformation', 'Transformation')}
            </ChakraText>
          </HStack>
        </VStack>

        {/* Additional spacer */}
        <Box />

        <VStack spacing="0.5em" alignItems="start">
          <HStack spacing="1em">
            <FlashBullet />
            <Box>
              <Trans t={t} i18nKey="example_url_endpoint_note_2">
                <ChakraText display="inline" color="appSlate">
                  You can also use a
                </ChakraText>{' '}
                <Link
                  href="https://docs.imagekit.io/testing-and-infrastructure-setup/using-custom-domain-name"
                  isExternal
                  display="inline"
                  color="appClearBlue"
                >
                  Custom Domain
                </Link>{' '}
                <ChakraText display="inline" color="appSlate">
                  e.g.
                </ChakraText>{' '}
                <ChakraText display="inline" color="appRed">
                  images.example.com
                </ChakraText>{' '}
                <ChakraText display="inline" color="appSlate">
                  instead of
                </ChakraText>{' '}
                <ChakraText display="inline" color="appRed">
                  ik.imagekit.io
                </ChakraText>
              </Trans>
            </Box>
          </HStack>
          <HStack spacing="1em">
            <FlashBullet />
            <Box>
              <Trans t={t} i18nKey="below_some_examples">
                <ChakraText display="inline" color="appSlate">
                  Below are some examples of real-time image transformations.
                  You can
                </ChakraText>
                <Link
                  isExternal
                  href="https://docs.imagekit.io/features/image-transformations"
                  display="inline"
                  color="appClearBlue"
                >
                  learn more
                </Link>
                <ChakraText display="inline" color="appSlate">
                  from the documentation.
                </ChakraText>
              </Trans>
            </Box>
          </HStack>
        </VStack>

        {/* Additional spacer */}
        <Box />

        <VStack spacing="0.5em" alignItems="start">
          <ChakraText fontWeight="semibold" color={colors.appBattleshipGrey}>
            {t(
              'master_image_auto_optimized',
              'Master Image (Automatically Optimized)',
            )}
          </ChakraText>
          <ChakraText color={colors.appSlate}>
            {`${user?.baseUrlForClientImageFetch}/${
              urlPrefix ? `${urlPrefix}/` : ''
            }rest/of/the/path/image.jpg`}
          </ChakraText>
        </VStack>
        <VStack spacing="0.5em" alignItems="start">
          <ChakraText fontWeight="semibold" color={colors.appBattleshipGrey}>
            {t('resized_300_image', 'Resized 300x300 image')}
          </ChakraText>
          <Box>
            <ChakraText display="inline" color={colors.appSlate}>
              {`${user?.baseUrlForClientImageFetch}/${
                urlPrefix ? `${urlPrefix}/` : ''
              }rest/of/the/path/image.jpg`}
            </ChakraText>
            <ChakraText display="inline" color={colors.appRed}>
              ?tr=h-300,w-300
            </ChakraText>
          </Box>
        </VStack>
        <VStack spacing="0.5em" alignItems="start">
          <ChakraText fontWeight="semibold" color={colors.appBattleshipGrey}>
            {t('blurred_image', 'Blurred image')}
          </ChakraText>
          <Box>
            <ChakraText display="inline" color={colors.appSlate}>
              {`${user?.baseUrlForClientImageFetch}/${
                urlPrefix ? `${urlPrefix}/` : ''
              }rest/of/the/path/image.jpg`}
            </ChakraText>
            <ChakraText display="inline" color={colors.appRed}>
              ?tr=bl-10
            </ChakraText>
          </Box>
        </VStack>

        {/* Additional spacer */}
        <Box />

        <ChakraText fontWeight="semibold" color={colors.appBattleshipGrey}>
          {t('quickly_get_started_SDKs', 'Quickly get started with our SDKs')}
        </ChakraText>
        <SDKIntegrationGrid columns={7} gap={6} iconSize="35px" fontSize="14px" />
      </VStack>
    </Box>
  )
}

export default ExampleEndpointUsage
